/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting, IForm } from 'app/shared/lib/essette-site';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/essette-api';
import { SystemReviewService } from 'app/cm/services/system-review-service';
import { ExpandEvent } from 'app/shared/elements/ess-table';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { IPopup } from 'app/shared/elements/popup-base';
import { SaveSystem } from 'app/cm/admin/system-illness/save-system';
import { validate } from 'app/shared/elements/ess-rule';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { Toaster } from 'app/shared/lib/toaster';
import * as CM from 'app/cm/models/essette-api';

@autoinject()
export class SystemIllness extends ComponentBase {

  ////****************** Fields and Properties ************************************////
  protected systems: ReadonlyArray<CM.System> = [];

  protected illnessDetails: Record<string, boolean> = {};

  private sorting = new Sorting();
  protected resultCount = 0;
  protected pageSize = 25;
  protected includeInactive = false;

  protected validate = validate;

  ////****************** Element References ***************************************////
  protected $pager!: EssPagerCustomElement;
  protected $form!: IForm;
  protected $saveSystemModal!: IPopup;
  protected $systemForm!: Element;
  protected $saveSystem!: SaveSystem;

  ////****************** Lifecycle events (in order of execution) *****************////
  constructor(
    private readonly service: SystemReviewService,
    private readonly toaster: Toaster,
    ...rest: any[]
  ) {
    super(...rest);

    this.applyDefaultSort();
  }

  protected bind(): void {
    this.reload();
  }

  ////****************** Events ***************************************************////

  /** Handles the grid's sort event */
  protected applySort(event: SortChangeEvent): void {
    const { property, direction } = event.detail;
    const { sorting } = this;
    if(direction === null) {
      sorting.remove(property);
      if(this.sorting.entries.length == 0) {
        this.applyDefaultSort();
      }
    } else {
      sorting.add({
        reference: property,
        direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
      });
    }

    this.reload();
    //Call stop Propagation to prevent parent table from reload and losing sort
    event.stopPropagation();
  }

  protected illnessDetailLoaded(event: ExpandEvent<CM.System>): void {
    const item = event.detail.item;
    const items = event.detail.items;

    if(item && items.includes(item)) {
      this.illnessDetails[item.id!] = true;
    }
  }

  protected formSuccess({ detail }: FormSuccessCustomEvent) {
    this.$saveSystemModal.close();

    if(detail.form.reset) detail.form.reset();

    // reload grid and reset page
    this.reload();
  }

  ////****************** Functions ************************************************////
  public async reload(): Promise<void> {
    await this.loadSystems();
  }

  protected applyDefaultSort(): void {
    this.sorting.entries = [
      {
        reference: 'description',
        direction: SortDirection.Ascending
      }
    ];
  }

  protected saveSystem(system: CM.System): void {
    this.$saveSystem.systemId = system?.id || null;
    this.$saveSystem.systemText = system?.description || '';
    this.$saveSystemModal.open();
  }

  protected saveSystemClose(): void {
    if(this.$form.reset) this.$form.reset();
    this.$saveSystemModal.close();
  }

  ////****************** Service Calls ********************************************////
  @apiCall
  private async loadSystems(): Promise<void> {
    const includes: string[] = [

    ];
    const fields: string[] = [
      "description"
    ];
    const options: Partial<IQueryLoadOptions> = {
      sorting: this.sorting,
      top: this.pageSize,
      page: this.$pager?.page || 0,
      includeInactive: this.includeInactive
    };

    try {
      this.state.beginLoad();

      const response = await this.service.loadSystems(options, includes, fields);
      this.systems = response.results;
      this.resultCount = response.count || 0;
    } catch(error) {
      throw error;
    } finally {
      this.state.endLoad();
    }
  }

  @apiCall
  protected async activateSystem(system: CM.System): Promise<void> {
    if(!system.id) return;

    // Show confirmation dialog only if deactivating
    if(system.isActive) {
      const result = await confirmation.showDeactivate('cm:system.$', 1, false);
      if(result.dialogResponse !== confirmation.options.OK) { return; }
    }

    let isSuccessful = false;
    try {
      this.state.beginSave();
      const response = await this.service.activateSystem(system.id, !system.isActive);
      isSuccessful = response.isSuccessful;
      await this.reload();
    }
    catch(error) {
      throw error;
    }
    finally {
      this.toaster.standard.activate('cm:system.$', !system.isActive, isSuccessful);
      this.state.endSave();
    }
  }
}
